// frontend/src/App.tsx - COMPLETE with Universal Header + Professional Styling

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { AuthProvider, useAuth } from './AuthContext';
import LoginForm from './LoginForm';
import ProtectedRoute from './ProtectedRoute';
import UserManagement from './UserManagement';
import ProcessSetup from './ProcessSetup';
import EvaluatorAssignment from './EvaluatorAssignment';
import EvaluationInterface from './EvaluationInterface';
import EvaluationGrid from './EvaluationGrid';
import EvaluatorProcessList from './EvaluatorProcessList';
import PriceEntry from './PriceEntry';
import EvaluatorSupplierList from './EvaluatorSupplierList';
import ComplianceCheck from './ComplianceCheck';
import ReportsDashboard from './ReportsDashboard';
import SuperAdminDashboard from './SuperAdminDashboard';
import SupplierManagement from './SupplierManagement';
import AppHeader from './AppHeader';
import AppFooter from './AppFooter';
import UserProfile from './UserProfile';
import PasswordReset from './PasswordReset';
import ForcePasswordChange from './ForcePasswordChange';
import COIReviewDashboard from './COIReviewDashboard';
import TenancyAdminDashboard from './TenancyAdminDashboard';
import ModerationIndicator from './ModerationIndicator';
import { API_URL } from './apiConfig';
import { extractErrorMessage, logError } from './utils/errorUtils';

// Types

interface Process {
  id: number;
  name: string;
  description: string | null;
  ito_reference: string;
  status: string;
  created_at: string;
  score_scale: string;
  supplier_count: number;
  evaluator_count?: number;
  is_compliance_complete?: boolean;
  has_pricing_entered?: boolean;
  has_compliance_criteria?: boolean;
  has_mandatory_requirements?: boolean;
  has_evaluation_criteria?: boolean;
  compliance_criteria_count?: number;
  mandatory_requirements_count?: number;
  main_criteria_count?: number;
  created_by?: number;
  updated_by?: number;
}

interface ProcessFormData {
  name: string;
  description: string;
  ito_reference: string;
}

// Main App Component Content
const AppContent: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();
  
  const [isConnected, setIsConnected] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [currentPage, setCurrentPage] = useState<
    'dashboard' | 'setup' | 'process-setup' | 'evaluator-assignment' | 
    'evaluator-process-list' | 'evaluator-supplier-list' | 'evaluate' | 'grid-evaluate' |
    'price-entry' | 'supplier-management' | 'compliance-check' | 'reports' | 'user-management' | 'super-admin' | 'profile' | 'coi-review' | 'tenancy-admin'
  >('dashboard');
  
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<{id: number, name: string} | null>(null);
  const [processes, setProcesses] = useState<Process[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const [evaluatorSupplierListKey, setEvaluatorSupplierListKey] = useState(0);
  const [evaluatorProcesses, setEvaluatorProcesses] = useState<any[]>([]);
  const [evaluatorProgress, setEvaluatorProgress] = useState<{[processId: number]: {completed: number, total: number}}>({});
  const [pendingCoiReviews, setPendingCoiReviews] = useState<number>(0);
  const [orgStats, setOrgStats] = useState<{totalUsers: number, activeUsers: number, organizationalUnits: number} | null>(null);
  
  // Define status order and styling configuration
  const statusConfig = {
    'DRAFT': { label: 'Draft Processes', color: '#6b7280', icon: '📝', defaultOpen: true },
    'ACTIVE': { label: 'Active Processes', color: '#f59e0b', icon: '🔄', defaultOpen: true },
    'EVALUATED': { label: 'Evaluated Processes', color: '#3b82f6', icon: '✅', defaultOpen: true },
    'COMPLETED': { label: 'Completed Processes', color: '#10b981', icon: '🏁', defaultOpen: false }
  };

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    // Default status groups (for backwards compatibility)
    Object.keys(statusConfig).forEach(status => {
      initial[status] = statusConfig[status as keyof typeof statusConfig].defaultOpen;
    });
    // User-based grouping defaults
    initial['my_group'] = true;  // My Processes expanded by default
    initial['team_group'] = false; // Team Processes collapsed by default
    // My Processes status groups expanded, Team Processes status groups collapsed
    Object.keys(statusConfig).forEach(status => {
      initial[`my_${status}`] = statusConfig[status as keyof typeof statusConfig].defaultOpen;
      initial[`team_${status}`] = false; // Team process statuses collapsed by default
    });
    return initial;
  });

  const [processForm, setProcessForm] = useState<ProcessFormData>({
    name: '',
    description: '',
    ito_reference: ''
  });

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  const toggleGroup = (status: string) => {
    setExpandedGroups(prev => ({ ...prev, [status]: !prev[status] }));
  };

  useEffect(() => {
    checkApiConnection();
    
    // Check for password reset token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setResetToken(token);
      setShowPasswordReset(true);
      // Clear URL parameters and redirect to base path
      window.history.replaceState({}, document.title, '/');
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && (user?.role as string) === 'SUPER_ADMIN' && currentPage === 'dashboard') {
      setCurrentPage('super-admin');
    }
    if (isAuthenticated && (user?.role as string) === 'ADMIN' && currentPage === 'dashboard') {
      console.log('Redirecting ADMIN user to tenancy-admin');
      setCurrentPage('tenancy-admin');
    }
  }, [isAuthenticated, user, currentPage]);

  const loadProcesses = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const token = Cookies.get('access_token');
      const response = await axios.get(`${API_URL}/api/processes/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Processs now come with criteria completion flags from backend
      setProcesses(response.data);
    } catch (error: any) {
      logError('Loading processes', error);
      showNotification('error', extractErrorMessage(error, 'Failed to load processes.'));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, showNotification]);


  useEffect(() => {
    if (isAuthenticated && user?.role !== 'EVALUATION_PANEL_MEMBER') {
      loadProcesses();
    }
  }, [isAuthenticated, user, loadProcesses]);


  const loadEvaluatorProcesses = useCallback(async () => {
    if (!user?.id || (user.role !== 'EVALUATION_PANEL_MEMBER' && user.role !== 'AUDITOR')) return;
    try {
      const token = Cookies.get('access_token');
      const response = await axios.get(`${API_URL}/api/evaluations/evaluator/${user.id}/processes`, { headers: { Authorization: `Bearer ${token}` } });
      setEvaluatorProcesses(response.data);
      const progressMap = response.data.reduce((acc: any, p: any) => ({ ...acc, [p.id]: { completed: p.evaluations_completed, total: p.total_compliant_suppliers } }), {});
      setEvaluatorProgress(progressMap);
    } catch (error) {
      console.error('Error loading evaluator processes:', error);
      showNotification('error', 'Failed to load your assigned processes.');
    }
  }, [user, showNotification]);

  useEffect(() => {
    if (user?.role === 'EVALUATION_PANEL_MEMBER'  || user?.role === 'AUDITOR') {
      loadEvaluatorProcesses();
    }
  }, [user, loadEvaluatorProcesses]);

  const loadPendingCoiReviews = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'PROCESS_OWNER') return;
    try {
      const token = Cookies.get('access_token');
      const response = await axios.get(`${API_URL}/api/coi/pending-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingCoiReviews(response.data.length || 0);
    } catch (error) {
      console.error('Error loading pending COI reviews:', error);
      // Don't show error notification for this background check
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'PROCESS_OWNER') {
      loadPendingCoiReviews();
    }
  }, [isAuthenticated, user, loadPendingCoiReviews]);

  const loadOrganizationalStats = useCallback(async () => {
    if (!isAuthenticated || (user?.role as string) !== 'ADMIN') return;
    try {
      const token = Cookies.get('access_token');
      const [usersResponse, unitsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/tenants/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/tenants/organizational-units`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const users = usersResponse.data;
      const activeUsers = users.filter((user: any) => user.is_active).length;
      
      setOrgStats({
        totalUsers: users.length,
        activeUsers: activeUsers,
        organizationalUnits: unitsResponse.data.length
      });
    } catch (error) {
      console.error('Error loading organizational stats:', error);
      // Don't show error notification for this background check
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && (user?.role as string) === 'ADMIN') {
      loadOrganizationalStats();
    }
  }, [isAuthenticated, user, loadOrganizationalStats]);

  const checkApiConnection = async () => {
    try {
      const response = await axios.get(`${API_URL}/`);
      setIsConnected(true);
    } catch (error) {
      console.error('❌ Failed to connect to EvaluTool API:', error);
      setIsConnected(false);
    }
  };

  const createProcess = async () => {
    setIsLoading(true);
    try {
      const token = Cookies.get('access_token');
      const response = await axios.post(`${API_URL}/api/processes/`, processForm, { headers: { Authorization: `Bearer ${token}` } });
      showNotification('success', `Process "${response.data.name}" created! Now, set up the criteria.`);
      await loadProcesses();
      setProcessForm({ name: '', description: '', ito_reference: '' });
      setSelectedProcess(response.data);
      setCurrentPage('process-setup');
    } catch (error: any) {
      logError('Creating process', error, processForm);
      showNotification('error', extractErrorMessage(error, 'Failed to create process'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    setCurrentPage('dashboard');
    setProcesses([]);
    setSelectedProcess(null);
    setSelectedSupplier(null);
    setEvaluatorSupplierListKey(prev => prev + 1);
  };

  const handlePasswordResetSuccess = () => {
    setShowPasswordReset(false);
    setResetToken(null);
    showNotification('success', 'Password reset successfully! You can now log in with your new password.');
  };

  const handleForcePasswordChangeSuccess = () => {
    // Refresh user data to get updated force_password_change flag
    window.location.reload();
  };

  const handleBackToDashboard = () => {
    if ((user?.role as string) === 'SUPER_ADMIN') {
      setCurrentPage('super-admin');
    } else if ((user?.role as string) === 'ADMIN') {
      setCurrentPage('tenancy-admin');
    } else {
      setCurrentPage('dashboard');
    }
    setSelectedProcess(null);
    setSelectedSupplier(null);
    loadProcesses();
  };

  const handleFormChange = (field: keyof ProcessFormData, value: string) => {
    setProcessForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Navigation handlers
  const handleReopenProcess = (process: Process) => {
    setSelectedProcess(process);
    setCurrentPage('process-setup');
    showNotification('success', `Reopened process "${process.name}" for editing`);
  };

  const handleEvaluatorProcessSelect = (processId: number, processName: string) => {
    setSelectedProcess({ id: processId, name: processName } as Process);
    setCurrentPage('evaluator-supplier-list');
  };


  const handleCloseProcess = async (process: Process) => {
    try {
      const token = Cookies.get('access_token');
      await axios.patch(`${API_URL}/api/processes/${process.id}/status`, 
        { status: 'COMPLETED' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showNotification('success', `Process "${process.name}" has been closed successfully!`);
      await loadProcesses();
    } catch (error: any) {
      console.error('Error closing process:', error);
      if (error.response?.status === 400) {
        showNotification('error', error.response.data?.detail || 'Process cannot be closed yet');
      } else {
        showNotification('error', 'Failed to close process. Please try again.');
      }
    }
  };

  const handleCancelProcess = async (process: Process) => {
    if (!window.confirm(`Are you sure you want to cancel "${process.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = Cookies.get('access_token');
      await axios.delete(`${API_URL}/api/processes/${process.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('success', `Process "${process.name}" has been cancelled and deleted.`);
      await loadProcesses();
    } catch (error: any) {
      console.error('Error cancelling process:', error);
      showNotification('error', 'Failed to cancel process. Please try again.');
    }
  };

  const handleViewProcess = (process: Process) => {
    setSelectedProcess(process);
    setCurrentPage('reports');
    showNotification('success', `Viewing completed process "${process.name}"`);
  };

  // NEW WORKFLOW-BASED BUTTON LOGIC
  const getProcessActionButtons = (process: Process) => {
    const buttons = [];
    const status = process.status.toUpperCase();

    const goToSetup = () => { 
      setSelectedProcess(process); 
      setCurrentPage('process-setup'); 
    };
    const goToAddSuppliers = () => { setSelectedProcess(process); setCurrentPage('supplier-management'); };
    const goToCompliance = () => { setSelectedProcess(process); setCurrentPage('compliance-check'); };
    const goToAssignEvaluators = () => { setSelectedProcess(process); setCurrentPage('evaluator-assignment'); };
    const goToPriceEntry = () => { setSelectedProcess(process); setCurrentPage('price-entry'); };
    const goToReports = () => { setSelectedProcess(process); setCurrentPage('reports'); };
    
    switch (status) {
      case 'DRAFT':
        // Check if all criteria are complete to show "Edit Process" vs "Continue Setup"
        const allCriteriaComplete = process.has_compliance_criteria && 
                                  process.has_mandatory_requirements && 
                                  process.has_evaluation_criteria;
        
        
        const setupButtonText = allCriteriaComplete ? '✏️ Edit Process' : '📝 Continue Setup';
        buttons.push({ text: setupButtonText, action: goToSetup, style: 'btn-primary-outline' });
        buttons.push({ text: `＋ ${process.supplier_count > 0 ? `${process.supplier_count} Suppliers Added` : 'Add Suppliers'}`, action: goToAddSuppliers, style: 'btn-primary' });
        buttons.push({ text: '🗑️ Cancel Process', action: () => handleCancelProcess(process), style: 'btn-destructive-outline' });
        break;
      case 'ACTIVE':
        if (process.is_compliance_complete) {
          buttons.push({ text: '✅ Compliance Complete', action: goToCompliance, style: 'btn-success', disabled: true });
        } else {
          buttons.push({ text: '📋 Run Compliance', action: goToCompliance, style: 'btn-warning' });
        }
        const evaluatorCount = process.evaluator_count || 0;
        buttons.push({ text: `👥 ${evaluatorCount > 0 ? `${evaluatorCount} Assigned` : 'Assign Evaluators'}`, action: goToAssignEvaluators, style: 'btn-secondary' });
        const pricingButtonText = process.has_pricing_entered ? '💰 Pricing Entered' : '💰 Enter Pricing';
        buttons.push({ text: pricingButtonText, action: goToPriceEntry, style: 'btn-secondary' });
        buttons.push({ text: '⚙️ Edit Setup', action: goToSetup, style: 'btn-secondary-outline' });
        break;
      case 'EVALUATED':
        // Allow pricing adjustments even in EVALUATED status
        const pricingButtonTextEvaluated = process.has_pricing_entered ? '💰 Pricing Entered' : '💰 Enter Pricing';
        buttons.push({ text: pricingButtonTextEvaluated, action: goToPriceEntry, style: 'btn-secondary' });
        buttons.push({ text: '🏁 Close Process', action: () => handleCloseProcess(process), style: 'btn-primary' });
        break;
      case 'COMPLETED':
        // Show pricing as view-only in completed processs
        const pricingButtonTextCompleted = process.has_pricing_entered ? '💰 View Pricing' : '💰 No Pricing';
        buttons.push({ text: pricingButtonTextCompleted, action: goToPriceEntry, style: 'btn-secondary-outline' });
        buttons.push({ text: '📋 View Details', action: goToReports, style: 'btn-secondary' });
        break;
    }

    if (status !== 'DRAFT') {
      buttons.push({ text: '📊 Reports', action: goToReports, style: 'btn-secondary-outline' });
    }

    return buttons;
  };

  // Utility functions from original


  const getEvaluatorProgressIndicator = (processId: number) => {
    const progress = evaluatorProgress[processId] || { completed: 0, total: 0 };
    const percentage = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
    
    return {
      completed: progress.completed,
      total: progress.total,
      percentage,
      color: percentage === 100 ? '#10b981' : percentage > 0 ? '#f59e0b' : '#ef4444'
    };
  };

  const getEvaluatorProcessButton = (process: any) => {
    const progress = getEvaluatorProgressIndicator(process.id);
    
    if (progress.percentage === 100) {
      return {
        text: '✅ Complete',
        color: '#10b981',
        action: () => handleEvaluatorProcessSelect(process.id, process.name)
      };
    } else if (progress.percentage > 0) {
      return {
        text: '⏸️ Resume',
        color: '#f59e0b',
        action: () => handleEvaluatorProcessSelect(process.id, process.name)
      };
    } else {
      return {
        text: '📝 Start',
        color: '#3b82f6',
        action: () => handleEvaluatorProcessSelect(process.id, process.name)
      };
    }
  };


  // STYLING OBJECT - PROFESSIONAL DESIGN SYSTEM
  const styles = {
    mainContainer: {
      minHeight: '100vh',
      background: 'var(--background-brand-gradient)',
      fontFamily: 'var(--font-family-primary)',
      color: 'var(--color-text-primary)'
    },
    card: {
      backgroundColor: 'var(--color-surface)',
      padding: 'var(--spacing-xl)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-md)',
      border: '1px solid var(--color-surface-variant)',
      marginBottom: 'var(--spacing-lg)'
    },
    input: {
      width: '100%',
      padding: '16px 20px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      fontSize: '1rem',
      transition: 'all 0.2s',
      outline: 'none',
      boxSizing: 'border-box' as const
    },
    label: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '0.5rem',
      display: 'block'
    },
  };

  // Show loading during auth initialization
  if (authLoading) {
    return (
      <div style={{...styles.mainContainer, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <div style={{...styles.card, textAlign: 'center', maxWidth: '400px'}}>
          <div className="loading-spinner" style={{ margin: '0 auto 1rem auto' }}></div>
          <div style={{ fontSize: '1.2rem', color: '#374151', marginBottom: '0.5rem' }}>
            Initialising EvaluTool...
          </div>
          <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
            Verifying authentication and loading your session
          </div>
        </div>
      </div>
    );
  }

  // Show password reset form if token is present
  if (showPasswordReset && resetToken) {
    return <PasswordReset token={resetToken} onSuccess={handlePasswordResetSuccess} />;
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <LoginForm />;
  }

  // Show force password change if user is authenticated but needs to change password
  if (isAuthenticated && user?.force_password_change) {
    return <ForcePasswordChange onSuccess={handleForcePasswordChangeSuccess} />;
  }

  // API Connection Check
  if (!isConnected) {
    return (
      <div style={{...styles.mainContainer, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <div style={{...styles.card, textAlign: 'center', maxWidth: '400px'}}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ color: '#1e293b', marginBottom: '1rem' }}>Connection Error</h2>
          <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
            Cannot connect to EvaluTool API. Please ensure the backend is running.
          </p>
          <button
            onClick={checkApiConnection}
            className="btn btn-primary"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // Notification Banner Component
  const NotificationBanner = () => {
    if (!notification) return null;
    return (
      <div style={{
        position: 'fixed', top: '1rem', right: '1rem', zIndex: 1000,
        background: notification.type === 'success' ? '#10b981' : '#ef4444',
        color: 'white', padding: '1rem 1.5rem', borderRadius: '12px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        maxWidth: '400px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <span>{notification.message}</span>
        <button onClick={() => setNotification(null)} style={{
          marginLeft: '1rem', background: 'none', border: 'none',
          color: 'white', cursor: 'pointer', fontSize: '1.5rem', lineHeight: 1
        }}>
          &times;
        </button>
      </div>
    );
  };

  // RENDER LOGIC with Universal Header
  const renderPageContent = () => {
    
    // Evaluation Panel Member role has a simple, separate view
    if (user?.role === 'EVALUATION_PANEL_MEMBER'  || user?.role === 'AUDITOR') {
      switch (currentPage) {
        case 'dashboard':
        case 'evaluator-process-list':
          return <ProtectedRoute requiredRole="EVALUATION_PANEL_MEMBER"><EvaluatorProcessList evaluatorId={user!.id} onProcessSelect={(id, name) => { setSelectedProcess({id, name} as Process); setCurrentPage('evaluator-supplier-list');}} onBack={handleBackToDashboard}/></ProtectedRoute>;
        case 'evaluator-supplier-list':
          return selectedProcess && <ProtectedRoute requiredRole="EVALUATION_PANEL_MEMBER"><EvaluatorSupplierList key={evaluatorSupplierListKey} processId={selectedProcess.id} processName={selectedProcess.name} evaluatorId={user!.id} onSupplierSelect={(id, name) => {setSelectedSupplier({id, name}); setCurrentPage('evaluate');}} onGridEvaluate={() => setCurrentPage('grid-evaluate')} onBack={() => setCurrentPage('dashboard')} /></ProtectedRoute>;
        case 'evaluate':
          return selectedProcess && selectedSupplier && <ProtectedRoute requiredRole="EVALUATION_PANEL_MEMBER"><EvaluationInterface processId={selectedProcess.id} supplierId={selectedSupplier.id} evaluatorId={user!.id} userRole={user!.role} onBack={() => {setCurrentPage('evaluator-supplier-list'); setEvaluatorSupplierListKey(k => k + 1);}} onComplete={handleBackToDashboard} /></ProtectedRoute>;
        case 'grid-evaluate':
          return selectedProcess && <ProtectedRoute requiredRole="EVALUATION_PANEL_MEMBER"><EvaluationGrid processId={selectedProcess.id} evaluatorId={user!.id} userRole={user!.role} onBack={() => {setCurrentPage('evaluator-supplier-list'); setEvaluatorSupplierListKey(k => k + 1);}} onComplete={handleBackToDashboard} /></ProtectedRoute>;
        case 'profile':
          return (
            <main style={{ padding: '2rem' }}>
              <UserProfile onBack={handleBackToDashboard} />
            </main>
          );
        default:
          return null;
      }
    }

    // Admin and PS roles
    switch (currentPage) {
      case 'dashboard':
        return (
          <main style={{ padding: '2rem' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <h2 className="page-title">
                {user?.role === 'PROCESS_OWNER' ? 'Process Owner Dashboard' : 
                 (user?.role as string) === 'ADMIN' ? 'Administrator Dashboard' : 
                 (user?.role as string) === 'SUPER_ADMIN' ? 'Super Administrator Dashboard' : 'Evaluator Dashboard'}
              </h2>

              {user?.role === 'PROCESS_OWNER' && (() => {
                // First group processs by user relationship (My vs Team)
                const myProcesses = processes.filter(process => 
                  process.created_by === user?.id
                );
                const teamProcesses = processes.filter(process => 
                  process.created_by !== user?.id && process.created_by != null
                );

                // Function to group processs by status
                const groupProcessesByStatus = (processList: Process[]) => {
                  return processList.reduce((groups, process) => {
                    const status = process.status;
                    if (!groups[status]) {
                      groups[status] = [];
                    }
                    groups[status].push(process);
                    return groups;
                  }, {} as Record<string, Process[]>);
                };

                const myGroupedProcesses = groupProcessesByStatus(myProcesses);
                const teamGroupedProcesses = groupProcessesByStatus(teamProcesses);

                // Function to render a process group (My Processes or Team Processes)
                const renderProcessGroup = (title: string, groupedProcesses: Record<string, Process[]>, keyPrefix: string) => {
                  const hasProcesses = Object.values(groupedProcesses).some(arr => arr.length > 0);
                  if (!hasProcesses) return null;

                  const userGroupKey = `${keyPrefix}_group`;
                  const isUserGroupExpanded = expandedGroups[userGroupKey] !== false; // Default open

                  return (
                    <div key={keyPrefix} style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      marginBottom: '1rem'
                    }}>
                      {/* User Group Header */}
                      <button
                        onClick={() => toggleGroup(userGroupKey)}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          background: '#f8fafc',
                          border: 'none',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          borderBottom: isUserGroupExpanded ? '1px solid #d1d5db' : 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontWeight: '700', color: '#374151', fontSize: '0.95rem' }}>{title}</span>
                          <span style={{
                            background: '#6b7280',
                            color: 'white',
                            padding: '0.125rem 0.375rem',
                            borderRadius: '8px',
                            fontSize: '0.7rem',
                            fontWeight: '600'
                          }}>
                            {Object.values(groupedProcesses).reduce((sum, arr) => sum + arr.length, 0)}
                          </span>
                        </div>
                        <span style={{
                          fontSize: '0.8rem',
                          color: '#6b7280',
                          transform: `rotate(${isUserGroupExpanded ? '90deg' : '0deg'})`,
                          transition: 'transform 0.2s ease'
                        }}>
                          ▶
                        </span>
                      </button>

                      {/* User Group Content */}
                      {isUserGroupExpanded && (
                        <div style={{ padding: '0.5rem' }}>
                          {Object.entries(statusConfig).map(([status, config]) => {
                            const statusProcesses = groupedProcesses[status] || [];
                            if (statusProcesses.length === 0) return null;

                            const statusKey = `${keyPrefix}_${status}`;
                            const isStatusExpanded = expandedGroups[statusKey] !== false; // Default open

                            return (
                              <div key={status} style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                marginBottom: '0.5rem'
                              }}>
                                {/* Status Group Header */}
                                <button
                                  onClick={() => toggleGroup(statusKey)}
                                  style={{
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    background: `linear-gradient(45deg, ${config.color}15, ${config.color}08)`,
                                    border: 'none',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    borderBottom: isStatusExpanded ? '1px solid #e5e7eb' : 'none'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={{ fontSize: '1rem' }}>{config.icon}</span>
                                    <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.85rem' }}>
                                      {config.label}
                                    </span>
                                    <span style={{
                                      background: config.color,
                                      color: 'white',
                                      padding: '0.125rem 0.375rem',
                                      borderRadius: '8px',
                                      fontSize: '0.7rem',
                                      fontWeight: '600'
                                    }}>
                                      {statusProcesses.length}
                                    </span>
                                  </div>
                                  <span style={{
                                    fontSize: '0.8rem',
                                    color: '#6b7280',
                                    transform: `rotate(${isStatusExpanded ? '90deg' : '0deg'})`,
                                    transition: 'transform 0.2s ease'
                                  }}>
                                    ▶
                                  </span>
                                </button>

                                {/* Status Group Content */}
                                {isStatusExpanded && (
                                  <div style={{ padding: '0.5rem' }}>
                                    {statusProcesses.map(process => (
                                      <div key={process.id} style={{
                                        background: '#f9fafb',
                                        padding: '1rem 1.5rem',
                                        borderRadius: '8px',
                                        border: '1px solid #e5e7eb',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        marginBottom: '0.5rem'
                                      }}>
                                        <div style={{ flex: 1 }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <h4 style={{ margin: 0, fontWeight: '600', color: '#1e293b' }}>{process.name}</h4>
                                            {(process.status === 'EVALUATED' || process.status === 'COMPLETED') && (
                                              <ModerationIndicator 
                                                processId={process.id} 
                                                size="small" 
                                                showText={false}
                                              />
                                            )}
                                          </div>
                                          <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
                                            {process.ito_reference} • Scale: {process.score_scale}
                                          </p>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                          {getProcessActionButtons(process).map(btn => (
                                            <button key={btn.text} onClick={btn.action} className={`btn btn-sm ${btn.style}`} disabled={btn.disabled}>{btn.text}</button>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <>
                    {/* Action buttons at top without card wrapper */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '1rem', 
                      marginBottom: '2rem',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }}>
                      {user?.role === 'PROCESS_OWNER' && (
                        <>
                          <button 
                            onClick={() => setCurrentPage('setup')} 
                            style={{
                              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                              color: 'white',
                              padding: '0.75rem 1.5rem',
                              borderRadius: '12px',
                              border: 'none',
                              fontWeight: '600',
                              fontSize: '0.95rem',
                              cursor: 'pointer',
                              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.5)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                            }}
                          >
                            🆕 Create New Process
                          </button>
                          <button 
                            onClick={() => setCurrentPage('coi-review')}
                            style={{
                              background: 'rgba(255, 255, 255, 0.9)',
                              color: '#374151',
                              padding: '0.75rem 1.25rem',
                              borderRadius: '12px',
                              border: '1px solid rgba(209, 213, 219, 0.8)',
                              fontWeight: '500',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              backdropFilter: 'blur(10px)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(249, 250, 251, 0.95)';
                              e.currentTarget.style.borderColor = '#9ca3af';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                              e.currentTarget.style.borderColor = 'rgba(209, 213, 219, 0.8)';
                            }}
                          >
                            ⚖️ COI Reviews
                          </button>
                        </>
                      )}
                      {(['ADMIN', 'SUPER_ADMIN'] as const).includes(user?.role as any) && (
                        <>
                          <button 
                            onClick={() => setCurrentPage('user-management')}
                            style={{
                              background: 'rgba(255, 255, 255, 0.9)',
                              color: '#374151',
                              padding: '0.75rem 1.25rem',
                              borderRadius: '12px',
                              border: '1px solid rgba(209, 213, 219, 0.8)',
                              fontWeight: '500',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              backdropFilter: 'blur(10px)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(249, 250, 251, 0.95)';
                              e.currentTarget.style.borderColor = '#9ca3af';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                              e.currentTarget.style.borderColor = 'rgba(209, 213, 219, 0.8)';
                            }}
                          >
                            👥 User Management
                          </button>
                          <button 
                            onClick={() => setCurrentPage('tenancy-admin')}
                            style={{
                              background: 'rgba(255, 255, 255, 0.9)',
                              color: '#374151',
                              padding: '0.75rem 1.25rem',
                              borderRadius: '12px',
                              border: '1px solid rgba(209, 213, 219, 0.8)',
                              fontWeight: '500',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              backdropFilter: 'blur(10px)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(249, 250, 251, 0.95)';
                              e.currentTarget.style.borderColor = '#9ca3af';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                              e.currentTarget.style.borderColor = 'rgba(209, 213, 219, 0.8)';
                            }}
                          >
                            🏢 Organisation Management
                          </button>
                        </>
                      )}
                      {(user?.role as string) === 'SUPER_ADMIN' && (
                        <>
                          <button 
                            onClick={() => setCurrentPage('super-admin')}
                            style={{
                              background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                              color: 'white',
                              padding: '0.75rem 1.5rem',
                              borderRadius: '12px',
                              border: 'none',
                              fontWeight: '600',
                              fontSize: '0.95rem',
                              cursor: 'pointer',
                              boxShadow: '0 4px 12px rgba(124, 58, 237, 0.4)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 6px 20px rgba(124, 58, 237, 0.5)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.4)';
                            }}
                          >
                            🛡️ Super Admin Dashboard
                          </button>
                        </>
                      )}
                    </div>

                    {/* ADMIN Dashboard Information */}
                    {(user?.role as string) === 'ADMIN' && (
                      <div style={styles.card}>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', color: '#1e293b' }}>
                          Administrator Dashboard
                        </h3>
                        <div style={{ 
                          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', 
                          padding: '1.5rem', 
                          borderRadius: '12px', 
                          border: '1px solid #e2e8f0',
                          marginBottom: '1rem' 
                        }}>
                          <p style={{ 
                            fontSize: '1rem', 
                            color: '#475569', 
                            marginBottom: '1rem', 
                            lineHeight: '1.6' 
                          }}>
                            As an Administrator, you manage the organisation's structure, branding, and user access. 
                            You do not have access to procurement processes or conflict of interest reviews - these are handled by Process Owners.
                          </p>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                            <div style={{ 
                              background: '#f1f5f9', 
                              padding: '1rem', 
                              borderRadius: '8px', 
                              border: '1px solid #cbd5e1' 
                            }}>
                              <h4 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#334155', marginBottom: '0.5rem' }}>
                                👥 User Management
                              </h4>
                              <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
                                Create, edit, and manage user accounts within your organisation
                              </p>
                            </div>
                            <div style={{ 
                              background: '#f0f9f4', 
                              padding: '1rem', 
                              borderRadius: '8px', 
                              border: '1px solid #bbf7d0' 
                            }}>
                              <h4 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#166534', marginBottom: '0.5rem' }}>
                                🏢 Organisation Management
                              </h4>
                              <p style={{ fontSize: '0.85rem', color: '#15803d', margin: 0 }}>
                                Configure branding, organisational structure, and hierarchies
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Organisation Summary Stats */}
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                          gap: '1rem', 
                          marginTop: '1.5rem' 
                        }}>
                          <div style={{ 
                            background: 'white', 
                            padding: '1.25rem', 
                            borderRadius: '12px', 
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' 
                          }}>
                            <h4 style={{ fontSize: '0.85rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>
                              TOTAL USERS
                            </h4>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e293b' }}>
                              {orgStats ? orgStats.totalUsers : '--'}
                            </div>
                            <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0.25rem 0 0 0' }}>
                              Across all roles
                            </p>
                          </div>
                          
                          <div style={{ 
                            background: 'white', 
                            padding: '1.25rem', 
                            borderRadius: '12px', 
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' 
                          }}>
                            <h4 style={{ fontSize: '0.85rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>
                              ACTIVE USERS
                            </h4>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#059669' }}>
                              {orgStats ? orgStats.activeUsers : '--'}
                            </div>
                            <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0.25rem 0 0 0' }}>
                              Currently enabled
                            </p>
                          </div>
                          
                          <div style={{ 
                            background: 'white', 
                            padding: '1.25rem', 
                            borderRadius: '12px', 
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' 
                          }}>
                            <h4 style={{ fontSize: '0.85rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>
                              ORG UNITS
                            </h4>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#7c3aed' }}>
                              {orgStats ? orgStats.organizationalUnits : '--'}
                            </div>
                            <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0.25rem 0 0 0' }}>
                              Departments/divisions
                            </p>
                          </div>
                          
                          <div style={{ 
                            background: 'white', 
                            padding: '1.25rem', 
                            borderRadius: '12px', 
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' 
                          }}>
                            <h4 style={{ fontSize: '0.85rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>
                              LOGO STATUS
                            </h4>
                            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#059669' }}>
                              ✅ Available
                            </div>
                            <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0.25rem 0 0 0' }}>
                              Organisation branding
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Processs section - Only for non-ADMINs */}
                    {(user?.role as string) !== 'ADMIN' && (
                      <div style={styles.card}>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', color: '#1e293b' }}>Processes</h3>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          {renderProcessGroup("My Processes", myGroupedProcesses, "my")}
                          {renderProcessGroup("Team Processes", teamGroupedProcesses, "team")}
                          
                        {/* Empty state when no processes */}
                        {processes.length === 0 && (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '3rem 1rem',
                            color: 'var(--color-text-secondary)',
                            backgroundColor: 'var(--color-surface-variant)',
                            borderRadius: 'var(--radius-lg)',
                            border: '2px dashed var(--color-border)'
                          }}>
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
                            <h3 style={{ 
                              fontSize: '1.2rem', 
                              marginBottom: '0.5rem', 
                              color: 'var(--color-text-primary)' 
                            }}>
                              No Processes Yet
                            </h3>
                            <p style={{ 
                              fontSize: '0.9rem', 
                              marginBottom: '1.5rem',
                              maxWidth: '500px',
                              margin: '0 auto 1.5rem auto'
                            }}>
                              Welcome to your new tenancy! Click "Create New Process" above to start your first procurement evaluation process.
                            </p>
                            <button 
                              onClick={() => setCurrentPage('setup')} 
                              className="btn btn-primary"
                              style={{
                                padding: '0.75rem 1.5rem',
                                fontSize: '1rem'
                              }}
                            >
                              🚀 Create Your First Process
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    )}
                  </>
                );
              })()}

            </div>
          </main>
        );
      case 'setup':
        return (
          <main style={{ padding: '2rem' }}>
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              <div style={styles.card}>
                <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: '#1e293b' }}>
                  New Procurement Process Details
                </h2>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label htmlFor="name" className="form-label required">Process Name</label>
                  <input id="name" type="text" value={processForm.name} onChange={(e) => handleFormChange('name', e.target.value)} placeholder="e.g., IT Services Procurement 2024" className="input" />
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label htmlFor="ito_reference" style={styles.label}>ITO Reference *</label>
                  <input id="ito_reference" type="text" value={processForm.ito_reference} onChange={(e) => handleFormChange('ito_reference', e.target.value)} placeholder="e.g., ITO-2024-001" className="input" />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label htmlFor="description" style={styles.label}>Description</label>
                  <textarea id="description" value={processForm.description} onChange={(e) => handleFormChange('description', e.target.value)} placeholder="Process description and requirements..." rows={4} className="textarea" />
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleBackToDashboard}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createProcess}
                    disabled={!processForm.name || !processForm.ito_reference || isLoading}
                    className={`btn btn-primary ${(!processForm.name || !processForm.ito_reference || isLoading) ? 'btn-disabled' : ''}`}
                    style={{
                      padding: '1rem 2rem',
                      fontSize: '1.1rem',
                    }}
                  >
                    {isLoading ? '⏳ Creating...' : '🚀 Create Process & Continue'}
                  </button>
                </div>
              </div>
            </div>
          </main>
        );
      case 'process-setup':
        const allCriteriaComplete = selectedProcess?.has_compliance_criteria && 
                                  selectedProcess?.has_mandatory_requirements && 
                                  selectedProcess?.has_evaluation_criteria;
        const viewMode = allCriteriaComplete ? 'summary' : 'setup';
        return selectedProcess && <ProcessSetup 
          processId={selectedProcess.id} 
          processName={selectedProcess.name} 
          onBack={handleBackToDashboard} 
          onComplete={handleBackToDashboard}
          viewMode={viewMode}
        />;
      case 'supplier-management':
        return selectedProcess && <SupplierManagement processId={selectedProcess.id} processName={selectedProcess.name} onBack={handleBackToDashboard} />;
      case 'compliance-check':
        return selectedProcess && <ComplianceCheck processId={selectedProcess.id} processName={selectedProcess.name} onComplete={handleBackToDashboard} onBack={handleBackToDashboard} />;
      case 'evaluator-assignment':
        return selectedProcess && <EvaluatorAssignment processId={selectedProcess.id} processName={selectedProcess.name} onComplete={handleBackToDashboard} onBack={handleBackToDashboard} />;
      case 'price-entry':
        return selectedProcess && <PriceEntry processId={selectedProcess.id} processName={selectedProcess.name} onComplete={handleBackToDashboard} onBack={handleBackToDashboard} />;
      case 'reports':
        return selectedProcess && <ReportsDashboard processId={selectedProcess.id} processName={selectedProcess.name} userRole={user!.role} onBack={handleBackToDashboard} />;
      case 'user-management':
        return <ProtectedRoute requiredRole="ADMIN"><UserManagement onBack={handleBackToDashboard} /></ProtectedRoute>;
      case 'coi-review':
        return <ProtectedRoute requiredRole="PROCESS_OWNER"><COIReviewDashboard onBack={handleBackToDashboard} /></ProtectedRoute>;
      case 'super-admin':
        return <ProtectedRoute requiredRole="SUPER_ADMIN"><SuperAdminDashboard onBack={handleBackToDashboard} /></ProtectedRoute>;
      case 'tenancy-admin':
        return <ProtectedRoute requiredRole="ADMIN"><TenancyAdminDashboard onBack={handleBackToDashboard} /></ProtectedRoute>;
      case 'profile':
        return (
          <main style={{ padding: '2rem' }}>
            <UserProfile onBack={handleBackToDashboard} />
          </main>
        );
      default:
        return <div>Page not found</div>;
    }
  };

  // Main render with Universal Header
  return (
    <div style={styles.mainContainer} className="density-compact">
      <NotificationBanner />
      <AppHeader
        currentPage={currentPage}
        onLogout={handleLogout}
        onBack={handleBackToDashboard}
        onProfile={() => {
          try {
            setCurrentPage('profile');
          } catch (error) {
            console.error('Error setting currentPage:', error);
          }
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 80px)' }}>
        {renderPageContent()}
        <AppFooter />
      </div>
    </div>
  );
};

// Root App Component
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;